import { supabase } from '@/lib/supabase';
import { formatNIS } from '@/utils/currency';

/** Matches live dgxn CHECK — task "revenue_share" maps to sub_discount (% / months). */
export type AffiliateRewardType = 'sub_discount' | 'ai_credits' | 'sub_credit_ils' | 'none';

export type AffiliateJoinPlanCode = 'ignite' | 'accelerate' | 'command' | 'business';

export interface AffiliateLink {
  id: string;
  code: string;
  token: string;
  label: string;
  referrer_org_id: string | null;
  partner_name: string | null;
  join_plan_code: AffiliateJoinPlanCode;
  join_free_days: number;
  reward_type: AffiliateRewardType;
  reward_ai_credits: number | null;
  reward_discount_percent: number | null;
  reward_discount_months: number | null;
  reward_credit_agorot: number | null;
  max_uses: number | null;
  use_count: number;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
}

export interface AffiliateLinkWithStats extends AffiliateLink {
  paid_conversions: number;
  referrer_org?: { id: string; name: string; slug: string } | null;
}

export type CreateAffiliateLinkInput = {
  label: string;
  code: string;
  referrer_org_id?: string | null;
  partner_name?: string | null;
  reward_type?: AffiliateRewardType;
  reward_discount_percent?: number | null;
  reward_discount_months?: number | null;
  reward_ai_credits?: number | null;
  reward_credit_agorot?: number | null;
  join_plan_code?: AffiliateJoinPlanCode;
  join_free_days?: number;
  max_uses?: number | null;
  expires_at?: string | null;
};

export type UpdateAffiliateLinkPatch = Partial<CreateAffiliateLinkInput>;

const CODE_RE = /^[A-Z0-9]{4,20}$/;

const PLAN_LABELS: Record<AffiliateJoinPlanCode, string> = {
  ignite: 'Ignite',
  accelerate: 'Accelerate',
  command: 'Command',
  business: 'Business',
};

export function affiliateLinkUrl(code: string): string {
  return `https://jumpstart.co.il/signup?ref=${encodeURIComponent(code)}`;
}

export function describeAffiliateReward(link: Pick<
  AffiliateLink,
  | 'reward_type'
  | 'reward_discount_percent'
  | 'reward_discount_months'
  | 'reward_ai_credits'
  | 'reward_credit_agorot'
>): string {
  switch (link.reward_type) {
    case 'sub_discount':
      return `${link.reward_discount_percent ?? 0}% / ${link.reward_discount_months ?? 0}mo`;
    case 'ai_credits':
      return `${link.reward_ai_credits ?? 0} AI credits`;
    case 'sub_credit_ils':
      return formatNIS(link.reward_credit_agorot ?? 0);
    case 'none':
      return 'None';
    default:
      return link.reward_type;
  }
}

export function describeAffiliateIncentive(link: Pick<AffiliateLink, 'join_plan_code' | 'join_free_days'>): string {
  const plan = PLAN_LABELS[link.join_plan_code] ?? link.join_plan_code;
  return `${plan} · ${link.join_free_days}d free`;
}

function normalizeCode(code: string): string {
  const normalized = code.trim().toUpperCase();
  if (!CODE_RE.test(normalized)) {
    throw new Error('Code must be 4–20 uppercase letters or digits');
  }
  return normalized;
}

async function attachStats(links: AffiliateLink[]): Promise<AffiliateLinkWithStats[]> {
  if (!links.length) return [];

  const linkIds = links.map((l) => l.id);
  const orgIds = [...new Set(links.map((l) => l.referrer_org_id).filter(Boolean))] as string[];

  const [convRes, orgRes] = await Promise.all([
    supabase
      .from('affiliate_referrals')
      .select('affiliate_link_id')
      .in('affiliate_link_id', linkIds)
      .in('status', ['active', 'expired']),
    orgIds.length
      ? supabase.from('organizations').select('id, name, slug').in('id', orgIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (convRes.error) throw convRes.error;
  if (orgRes.error) throw orgRes.error;

  const paidByLink = new Map<string, number>();
  for (const row of convRes.data ?? []) {
    const id = row.affiliate_link_id as string;
    paidByLink.set(id, (paidByLink.get(id) ?? 0) + 1);
  }

  const orgById = new Map(
    (orgRes.data ?? []).map((o: { id: string; name: string; slug: string }) => [o.id, o]),
  );

  return links.map((link) => ({
    ...link,
    paid_conversions: paidByLink.get(link.id) ?? 0,
    referrer_org: link.referrer_org_id ? orgById.get(link.referrer_org_id) ?? null : null,
  }));
}

function buildInsertRow(input: CreateAffiliateLinkInput): Record<string, unknown> {
  const rewardType = input.reward_type ?? 'sub_discount';
  return {
    label: input.label.trim(),
    code: normalizeCode(input.code),
    referrer_org_id: input.referrer_org_id ?? null,
    partner_name: input.partner_name?.trim() || null,
    reward_type: rewardType,
    reward_discount_percent:
      rewardType === 'sub_discount' ? (input.reward_discount_percent ?? 5) : null,
    reward_discount_months:
      rewardType === 'sub_discount' ? (input.reward_discount_months ?? 12) : null,
    reward_ai_credits: rewardType === 'ai_credits' ? (input.reward_ai_credits ?? null) : null,
    reward_credit_agorot:
      rewardType === 'sub_credit_ils' ? (input.reward_credit_agorot ?? null) : null,
    join_plan_code: input.join_plan_code ?? 'ignite',
    join_free_days: input.join_free_days ?? 30,
    max_uses: input.max_uses ?? null,
    expires_at: input.expires_at || null,
    is_active: true,
  };
}

export const affiliatesApi = {
  listLinks: async (opts?: { referrerOrgId?: string }): Promise<AffiliateLinkWithStats[]> => {
    let query = supabase.from('affiliate_links').select('*').order('created_at', { ascending: false });
    if (opts?.referrerOrgId) {
      query = query.eq('referrer_org_id', opts.referrerOrgId);
    }
    const { data, error } = await query;
    if (error) throw error;
    return attachStats((data ?? []) as AffiliateLink[]);
  },

  createLink: async (input: CreateAffiliateLinkInput): Promise<AffiliateLink> => {
    const code = normalizeCode(input.code);
    const { data: existing, error: dupErr } = await supabase
      .from('affiliate_links')
      .select('id')
      .eq('code', code)
      .maybeSingle();
    if (dupErr) throw dupErr;
    if (existing) throw new Error('Code already in use');

    const row = buildInsertRow({ ...input, code });
    const { data, error } = await supabase.from('affiliate_links').insert(row).select('*').single();
    if (error) throw error;
    return data as AffiliateLink;
  },

  updateLink: async (id: string, patch: UpdateAffiliateLinkPatch): Promise<AffiliateLink> => {
    const updates: Record<string, unknown> = {};
    if (patch.label !== undefined) updates.label = patch.label.trim();
    if (patch.code !== undefined) updates.code = normalizeCode(patch.code);
    if (patch.referrer_org_id !== undefined) updates.referrer_org_id = patch.referrer_org_id;
    if (patch.partner_name !== undefined) updates.partner_name = patch.partner_name?.trim() || null;
    if (patch.join_plan_code !== undefined) updates.join_plan_code = patch.join_plan_code;
    if (patch.join_free_days !== undefined) updates.join_free_days = patch.join_free_days;
    if (patch.max_uses !== undefined) updates.max_uses = patch.max_uses;
    if (patch.expires_at !== undefined) updates.expires_at = patch.expires_at || null;

    if (patch.reward_type !== undefined) {
      updates.reward_type = patch.reward_type;
      updates.reward_discount_percent =
        patch.reward_type === 'sub_discount' ? (patch.reward_discount_percent ?? 5) : null;
      updates.reward_discount_months =
        patch.reward_type === 'sub_discount' ? (patch.reward_discount_months ?? 12) : null;
      updates.reward_ai_credits =
        patch.reward_type === 'ai_credits' ? (patch.reward_ai_credits ?? null) : null;
      updates.reward_credit_agorot =
        patch.reward_type === 'sub_credit_ils' ? (patch.reward_credit_agorot ?? null) : null;
    } else {
      if (patch.reward_discount_percent !== undefined) {
        updates.reward_discount_percent = patch.reward_discount_percent;
      }
      if (patch.reward_discount_months !== undefined) {
        updates.reward_discount_months = patch.reward_discount_months;
      }
      if (patch.reward_ai_credits !== undefined) updates.reward_ai_credits = patch.reward_ai_credits;
      if (patch.reward_credit_agorot !== undefined) {
        updates.reward_credit_agorot = patch.reward_credit_agorot;
      }
    }

    const { data, error } = await supabase
      .from('affiliate_links')
      .update(updates)
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw error;
    return data as AffiliateLink;
  },

  toggleActive: async (id: string, isActive: boolean): Promise<void> => {
    const { error } = await supabase.from('affiliate_links').update({ is_active: isActive }).eq('id', id);
    if (error) throw error;
  },
};
